'use strict';

import React, {Component} from 'react';
import {connect} from 'react-redux';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import {keys} from 'ts-transformer-keys';
import {enumerate} from 'ts-transformer-enumerate';

import {Button} from 'primereact/button';
import {Dialog} from 'primereact/dialog';
import {Toast} from 'primereact/toast';

import Joi from '~/shared/src/utilities/joiExtended';
import {__} from '~/shared/src/utilities/messageCatalog';
import User from '~/shared/src/model/User';
import {requestBaseParametersSchema} from '~/shared/src/model/BaseRequest';
import {panelSchema} from '~/shared/src/validationSchemes/panel';
import {ToJsonResult as ErrorJson} from '~/shared/src/utilities/error';
import StackError, {ToJsonResult as StackErrorJson} from '~/shared/src/base/StackError';
import LocalizableError, {ToJsonResult as LocalizableErrorJson} from '~/shared/src/base/LocalizableError';
import {
    UploadXmlImportResult,
    XmlImportStatistic
} from '~/shared/src/utilities/modules/xmlImport';

import Toolbar from '~/frontend/src/components/base/XmlImport/Toolbar';
import FileUploadPanel from '~/frontend/src/components/base/XmlImport/FileUpload';
import InputPanel from '~/frontend/src/components/base/XmlImport/Input';

import {
    AppDispatch,
    StoreState,
    Thunk
} from '~/frontend/src/store';
import {XhrComponentState} from '~/frontend/src/reducers/xhr';
import * as PanelManagerActions from '~/frontend/src/actions/panelManager';
import {ClosePanel} from '~/frontend/src/actions/panelManager/closePanel';
import {SetPanelDirty} from '~/frontend/src/actions/panelManager/setPanelDirty';
import {SetPanelInvalid} from '~/frontend/src/actions/panelManager/setPanelInvalid';
import {SetPanelInfo} from '~/frontend/src/actions/panelManager/setPanelInfo';
import hideConfirmCloseDialog from '~/frontend/src/actions/panelManager/hideConfirmCloseDialog';
import * as PanelActions from '~/frontend/src/actions/panel/common';
import setParamOrig, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import uploadData, {UploadData} from '~/frontend/src/actions/panel/xmlImport/uploadData';
import {
    setElusiveData as setElusiveDataOrig,
    SetElusiveDataAction,
    unsetElusiveData,
    UnsetElusiveDataAction,
    clearElusiveData,
    ClearElusiveData
} from '~/frontend/src/actions/elusiveData';
import * as IntercomActions from '~/frontend/src/actions/intercom';
import {
    MsgParams,
    SetMessage,
    UnsetMessage
} from '~/frontend/src/actions/intercom';
import {PanelIntercomState} from '~/frontend/src/reducers/intercom';

export type LogEntry = {
    timestamp : number;
    severity : 'debug' | 'info' | 'error' | 'warning';
    file : string;
    message : string;
};

type PanelElusiveData = PanelErrorData & PanelDirtyData & {
    xml? : string | string[];
    showCouldNotUploadPopup? : boolean;
    log? : {
        fileUpload? : LogEntry[];
        input? : LogEntry[];
    };
};

type Panel = 'fileUpload' | 'input';
const panels = enumerate<Panel>();

export type FileUploadValue = {
    id : string;
    name : string;
    size : number;
    content : string;
    selected : boolean;
    status : 'pending' | 'imported' | 'error' | 'warning';
}

export type DefaultProps = {
    activePanel? : Panel;
    inputValue? : string;
    showInputErrorPopup? : boolean;
    fileUploadValue? : FileUploadValue[];
};
export const defaultProps : Required<DefaultProps> = {
    activePanel        : 'input',
    inputValue         : '',
    showInputErrorPopup: false,
    fileUploadValue    : []
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    schema : Schema;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type StateProps = {

    /**
     * The intercom messages for this component.
     *
     * Will be mapped from global state.
     */
    intercomMessages? : PanelIntercomState;

    /**
     * The running requests belonging to this component (uid).
     *
     * Will be mapped from global state.
     */
    xhrRequestsForComponent? : XhrComponentState;

    /**
     * The elusive data (not persisted by redux store) for this component.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveData;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

const Actions = Object.assign( {}, PanelManagerActions, PanelActions, { uploadData, clearElusiveData, setElusiveData: setElusiveDataOrig, unsetElusiveData }, IntercomActions ) as any;
type SetParam = <K extends keyof ( ComponentProps )>( panelKey : string, paramKey : K, paramValue : ComponentProps[K] ) => SetParamAction;
const setParam = setParamOrig as SetParam;
type UnsetParam = ( panelKey : string, paramKey : keyof ( ComponentProps ) ) => UnsetParamAction;
type SetParams = ( panelKey : string, panelProps : Partial<ComponentProps> ) => SetParamsAction;
type SetElusiveData = ( panelKey : string, data : PanelElusiveData ) => SetElusiveDataAction;
const setElusiveData = setElusiveDataOrig as SetElusiveData;
type UnsetElusiveData = ( panelKey : string, keys : ( keyof PanelElusiveData )[] ) => UnsetElusiveDataAction;
type ReplaceReturnType<T extends ( ...a : any ) => any, TNewReturn> = ( ...a : Parameters<T> ) => TNewReturn;

type ActionPropsRaw = {
    // panel manager actions
    closePanel : ClosePanel;
    setPanelDirty : SetPanelDirty;
    setPanelInvalid : SetPanelInvalid;
    setPanelInfo : SetPanelInfo;

    // common panel actions
    setParam : SetParam;
    setParams : SetParams;
    unsetParam : UnsetParam;

    // panel actions
    uploadData : UploadData;

    // elusiveData actions
    setElusiveData : SetElusiveData;
    unsetElusiveData : UnsetElusiveData;
    clearElusiveData : ClearElusiveData;

    // intercom actions
    setMessage : SetMessage;
    unsetMessage : UnsetMessage;
};
type ActionProps = {
    [K in keyof ActionPropsRaw] : ReplaceReturnType<ActionPropsRaw[K], ReturnType<ActionPropsRaw[K]> extends Thunk<any> ? ReturnType<ReturnType<ActionPropsRaw[K]>> : void>;
};

export type AllProps = Required<DefaultProps> & Props & StateProps & ActionProps;

type State = {
    errorCount : number;
    errorTooltip? : string | JSX.Element | ( string | JSX.Element )[];
    errorMessage? : string | JSX.Element | ( string | JSX.Element )[];
};


class XmlImport extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    // panel manager config
    static panelType = 'XmlImport' as string;
    static panelTabTitle = 'XmlImport';
    static panelTabIcon = 'pi pi-file-import' as string;

    static panelPropsSchemaMsgIdPrefix = 'YN.base.xmlImport';
    static panelPropsSchema = Joi.object()
                                 .concat( panelSchema )
                                 .concat( Joi.object( {
                                     // panelSchema defaults
                                     panelType    : Joi.string().valid( XmlImport.panelType ),
                                     panelTabTitle: Joi.string().default( XmlImport.panelTabTitle ),
                                     panelTabIcon : Joi.string().default( XmlImport.panelTabIcon )
                                 } ) )
                                 .concat( requestBaseParametersSchema )
                                 .concat( Joi.object( {
                                     activePanel: Joi.string().valid( ...panels )
                                 } ) );

    /**
     * YOONITE lifecycle method
     */
    static onClosePanelAction( props : ComponentProps ) {
        // backend request to clear cached overview configuration data
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {

        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSavePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            await XmlImport.uploadPanelData(
                dispatch,
                getState,
                props
            );
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSaveAndClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {

        }
    }

    /**
     * YOONITE lifecycle method
     */
    static async uploadPanelData( dispatch : AppDispatch, getState : () => StoreState, props : ComponentProps, successMsgParams : MsgParams | undefined = undefined ) : Promise<UploadXmlImportResult | false> {
        const propsWithDefaults : Required<DefaultProps> & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  uid,
                  activePanel
              }                                                  = propsWithDefaults,
              panelElusiveData                                   = getState()?.elusiveData?.[ `${uid}_${activePanel}Panel` ],
              errorData                                          = panelElusiveData?.errorData as PanelErrorData[ 'errorData' ],
              xml                                                = panelElusiveData?.xml ?? '' as string | string[];

        // panel has errors -> show popup
        if ( errorData?.count ) {
            dispatch( hideConfirmCloseDialog( uid ) );
            dispatch( setElusiveData( uid, { showCouldNotUploadPopup: true } ) );

            return Promise.resolve( false );
        }

        // panel has no errors -> save
        else {
            const uploadDataHandler     = async ( ...args : Parameters<UploadData> ) => {
                      return dispatch( uploadData( ...args ) );
                  },
                  setParamHandler       = ( ...args : Parameters<SetParam> ) => {
                      dispatch( setParam( ...args ) );
                  },
                  setElusiveDataHandler = ( ...args : Parameters<SetElusiveData> ) => {
                      dispatch( setElusiveData( ...args ) );
                  };

            return XmlImport.doUpload(
                propsWithDefaults,
                ( getState()?.elusiveData?.[ uid ] as PanelElusiveData | undefined ),
                xml,
                uploadDataHandler,
                setParamHandler,
                setElusiveDataHandler,
                successMsgParams
            );
        }
    }

    static async doUpload( props : ComponentProps, elusiveData : PanelElusiveData = {}, xml : string | string[], uploadDataHandler : ActionProps['uploadData'], setParamHandler : ActionProps['setParam'], setElusiveDataHandler : ActionProps['setElusiveData'], successMsgParams : MsgParams | undefined = undefined ) : Promise<UploadXmlImportResult | false> {
        const {
                  uid,
                  activePanel,
                  fileUploadValue,
                  inputValue
              } = props;

        let elusiveDataToSet : Partial<PanelElusiveData> = {
            log: {
                fileUpload: ( [] as LogEntry[] ).concat( elusiveData.log?.fileUpload ?? [] ),
                input     : ( [] as LogEntry[] ).concat( elusiveData.log?.input ?? [] )
            }
        };

        if ( activePanel === 'fileUpload' && fileUploadValue ) {
            for ( const file of fileUploadValue ) {
                if ( file.selected ) {
                    elusiveDataToSet.log?.fileUpload?.push( {
                        timestamp: Date.now(),
                        severity : 'info',
                        file     : file.name,
                        message  : 'uploading...'
                    } );
                }
            }
        }
        else if ( activePanel === 'input' && inputValue ) {
            elusiveDataToSet.log?.input?.push( {
                timestamp: Date.now(),
                severity : 'info',
                file     : 'input',
                message  : 'uploading...'
            } );
        }

        setElusiveDataHandler( uid, elusiveDataToSet );

        // clone for next update
        elusiveDataToSet = {
            log: {
                fileUpload: ( [] as LogEntry[] ).concat( elusiveDataToSet.log?.fileUpload ?? [] ),
                input     : ( [] as LogEntry[] ).concat( elusiveDataToSet.log?.input ?? [] )
            }
        };

        const result = await uploadDataHandler( uid, xml, successMsgParams );

        console.log( 'result', result );

        if ( result !== false ) {
            if ( activePanel === 'fileUpload' && fileUploadValue ) {
                const value = ( [] as typeof fileUploadValue ).concat( fileUploadValue );

                let fileCount = 0;

                for ( let i = 0; i < value.length; i++ ) {
                    const file = value[ i ];

                    if ( file.selected ) {
                        fileCount++;

                        if ( result.success ) {
                            // deselect file
                            value[ i ] = {
                                ...file,
                                selected: false,
                                status  : 'imported'
                            };

                            // write warnings if
                            if ( result.warnings?.[ fileCount - 1 ] ) {
                                const warningData = result.warnings[ fileCount - 1 ];

                                console.log( 'warningData', warningData );

                                let warningString = '';

                                value[ i ].status = 'warning';

                                if ( warningData ) {
                                    if ( typeof warningData === 'string' ) {
                                        warningString = warningData;
                                    }
                                    else if ( ( warningData as StackErrorJson ).type === 'stack' ) {
                                        warningString = StackError.fromJSON( warningData as StackErrorJson ).toString( 0 );
                                    }
                                    else if ( ( warningData as LocalizableErrorJson ).type === 'localizable' ) {
                                        warningString = LocalizableError.fromJSON( warningData as LocalizableErrorJson ).toHTML();
                                    }
                                    else {
                                        warningString = Error.fromJSON( warningData as ErrorJson ).toHTML();
                                    }
                                }

                                elusiveDataToSet.log?.fileUpload?.push( {
                                    timestamp: Date.now(),
                                    severity : 'warning',
                                    file     : file.name,
                                    message  : warningString
                                } );
                            }

                            // write statistics to log
                            if ( result.data[ fileCount - 1 ] ) {
                                elusiveDataToSet.log?.fileUpload?.push( {
                                    timestamp: Date.now(),
                                    severity : 'info',
                                    file     : file.name,
                                    message  : XmlImport.parseStatistics( result.data[ fileCount - 1 ] )
                                } );
                            }
                        }

                        // mark erroneous file
                        else if ( result.data?.[ fileCount - 1 ] ) {
                            const errorData = ( result.data as ( string | ErrorJson | StackErrorJson | LocalizableErrorJson | undefined )[] )[ fileCount - 1 ];

                            let errorString = 'not imported';

                            value[ i ] = {
                                ...file,
                                status: 'error'
                            };

                            // write errors to log
                            if ( errorData ) {
                                if ( typeof errorData === 'string' ) {
                                    errorString = errorData;
                                }
                                else if ( ( errorData as StackErrorJson ).type === 'stack' ) {
                                    errorString = StackError.fromJSON( errorData as StackErrorJson ).toString( 0 );
                                }
                                else if ( ( errorData as LocalizableErrorJson ).type === 'localizable' ) {
                                    errorString = LocalizableError.fromJSON( errorData as LocalizableErrorJson ).toHTML();
                                }
                                else {
                                    errorString = Error.fromJSON( errorData as ErrorJson ).toHTML();
                                }
                            }

                            elusiveDataToSet.log?.fileUpload?.push( {
                                timestamp: Date.now(),
                                severity : 'error',
                                file     : file.name,
                                message  : errorString
                            } );
                        }
                    }
                }

                setParamHandler( uid, 'fileUploadValue', value );
            }
            else if ( activePanel === 'input' ) {

                // write warnings and statistics to log
                if ( result.success ) {
                    // write warings if
                    if ( result.warnings?.[ 0 ] ) {
                        const warningData = result.warnings[ 0 ];
                        let warningString = '';

                        if ( warningData ) {
                            if ( typeof warningData === 'string' ) {
                                warningString = warningData;
                            }
                            else if ( ( warningData as StackErrorJson ).type === 'stack' ) {
                                warningString = StackError.fromJSON( warningData as StackErrorJson ).toString( 0 );
                            }
                            else if ( ( warningData as LocalizableErrorJson ).type === 'localizable' ) {
                                warningString = LocalizableError.fromJSON( warningData as LocalizableErrorJson ).toHTML();
                            }
                            else {
                                warningString = Error.fromJSON( warningData as ErrorJson ).toHTML();
                            }
                        }

                        elusiveDataToSet.log?.input?.push( {
                            timestamp: Date.now(),
                            severity : 'warning',
                            file     : 'input',
                            message  : warningString
                        } );
                    }

                    // write statistics to log
                    if ( result.data[ 0 ] ) {
                        elusiveDataToSet.log?.input?.push( {
                            timestamp: Date.now(),
                            severity : 'info',
                            file     : 'input',
                            message  : XmlImport.parseStatistics( result.data[ 0 ] )
                        } );
                    }
                }

                // write error to log
                else if ( result.data?.[ 0 ] ) {
                    const errorData = ( result.data as ( string | ErrorJson | StackErrorJson | LocalizableErrorJson | undefined )[] )[ 0 ];

                    let errorString = 'not imported';

                    if ( errorData ) {
                        if ( typeof errorData === 'string' ) {
                            errorString = errorData;
                        }
                        else if ( ( errorData as StackErrorJson ).type === 'stack' ) {
                            errorString = StackError.fromJSON( errorData as StackErrorJson ).toString( 0 );
                        }
                        else if ( ( errorData as LocalizableErrorJson ).type === 'localizable' ) {
                            errorString = LocalizableError.fromJSON( errorData as LocalizableErrorJson ).toHTML();
                        }
                        else {
                            errorString = Error.fromJSON( errorData as ErrorJson ).toHTML();
                        }
                    }

                    elusiveDataToSet.log?.input?.push( {
                        timestamp: Date.now(),
                        severity : 'error',
                        file     : 'input',
                        message  : errorString
                    } );
                }
            }
        }

        setElusiveDataHandler( uid, elusiveDataToSet );

        return Promise.resolve( result );
    }

    static parseStatistics( statistics : XmlImportStatistic[] ) : string {
        let retVal : string = '';

        for ( const statistic of statistics ) {
            const statusOrder               = [ 'insert', 'update', 'delete' ],
                  insertUpdateDeleteSortFnc = ( status1 : string, status2 : string ) => {
                      const s1 = statusOrder.findIndex( s => s === status1 ),
                            s2 = statusOrder.findIndex( s => s === status2 );

                      if ( s1 > s2 ) {
                          return 1;
                      }
                      else if ( s2 > s1 ) {
                          return -1;
                      }
                      else {
                          return 0;
                      }
                  },
                  typeKeys                  = Object.keys( statistic.statistics ).sort();

            if ( !retVal ) {
                retVal += 'statistics'
            }
            retVal += '\n' + `${statistic.section} schema='${statistic.schema}`.indent();

            for ( const type of typeKeys ) {
                // @ts-expect-error - type is really a key of statistics.statistics
                const statusKeys = ( Object.keys( statistic.statistics[ type ] ) as string[] ).sort( insertUpdateDeleteSortFnc );

                retVal += '\n' + ( type as string ).indent( 2 );

                for ( const status of statusKeys ) {
                    if ( status !== 'property' ) {
                        // @ts-expect-error - type is really a key of statistics.statistics
                        retVal += '\n' + ( ( status as string ) + ': ' + statistic.statistics[ type ][ status ] ).indent( 3 );
                    }
                }
                // @ts-expect-error - type is really a key of statistics.statistics
                if ( statistic.statistics[ type ][ 'property' ] ) {
                    // @ts-expect-error - type is really a key of statistics.statistics
                    const propertyStatusKeys = ( Object.keys( statistic.statistics[ type ][ 'property' ] ) as string[] ).sort( insertUpdateDeleteSortFnc );

                    if ( type === 'item' ) {
                        retVal += '\n' + 'property'.indent( 2 );
                    }
                    else {
                        retVal += '\n' + 'reference property'.indent( 2 );
                    }

                    for ( const status of propertyStatusKeys ) {
                        // @ts-expect-error - type is really a key of statistics.statistics
                        retVal += '\n' + ( ( status as string ) + ': ' + statistic.statistics[ type ][ 'property' ][ status ] ).indent( 3 );
                    }
                }
            }
        }

        return retVal;
    }

    /**
     * YOONITE lifecycle method
     */
    static buildPanelPathFromProps( props : AllProps ) : string {
        return `#${XmlImport.panelType}/${props.activePanel}`;

    }

    /**
     * YOONITE lifecycle method
     */
    static async buildPanelPropsFromPath( path : string ) : Promise<Partial<ComponentProps>> {
        const retVal : Partial<ComponentProps> = {},
              parts                            = path.match( /#([^?]*)(\?(.*))?/ ),
              params                           = parts?.[ 1 ]?.split( '/' ) ?? [],
              stackError                       = new StackError( 'YN.base.resolvePanelPropsFromURL.error.title', { path: path } );

        // params[ 0 ] = panelType

        // params[ 1 ] = activePanel
        if ( params?.[ 1 ] ) {
            retVal.activePanel = params[ 1 ] as Panel;
        }
        else {
            stackError.add( new LocalizableError( 'YN.base.xmlImport.resolvePanelPropsFromURL.error.couldNotResolveActivePanel' ) );
        }

        if ( stackError.hasErrors() ) {
            throw ( stackError.toString() );
        }

        return retVal;
    }

    toast : Toast | null = null;
    toastTopOffset : number = 55;
    toastBottomOffset : number = 32;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            errorCount: 0
        };

        this.onActivePanelChange = this.onActivePanelChange.bind( this );
        this.onFileUploadValueChange = this.onFileUploadValueChange.bind( this );
        this.onInputValueChange = this.onInputValueChange.bind( this );
        this.onUpload = this.onUpload.bind( this );
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : AllProps ) : Partial<State> {
        const {
                  elusiveData
              }                       = props,
              retVal : Partial<State> = {
                  errorCount  : 0,
                  errorTooltip: undefined,
                  errorMessage: undefined
              };

        // errors
        if ( elusiveData?.errorData?.count ) {
            retVal.errorCount = elusiveData.errorData.count;

            retVal.errorTooltip = <div key="error" className="yn-tooltip-error">
                <div className="yn-tooltip-header">
                    {__( 'YN.base.common.tooltip.error.title' )}
                </div>
                {elusiveData.errorData.tooltip}
            </div>;

            retVal.errorMessage = <>
                <div className="yn-error-message">
                    {__( `YN.base.xmlImport.${props.activePanel}.error.title` )}
                </div>
                <hr className="yn-error-ruler"/>
                <div className="yn-error-suberror">
                    {elusiveData.errorData.message}
                </div>
            </>;
        }

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        this.afterRender();
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : AllProps ) {
        this.afterRender();
    }


    /* event handler **********************************************************************************************************************/

    onActivePanelChange( activePanel : DefaultProps[ 'activePanel' ] ) {
        this.props.setParam( this.props.uid, 'activePanel', activePanel );
    }

    onFileUploadValueChange( fileUploadValue : FileUploadValue[] ) {
        this.props.setParam( this.props.uid, 'fileUploadValue', fileUploadValue );
    }

    onInputValueChange( inputValue : string ) {
        const {
                  uid,
                  elusiveData
              } = this.props;

        this.props.setParam( uid, 'inputValue', inputValue );

        this.props.setElusiveData( uid, {
            log: {
                fileUpload: elusiveData?.log?.fileUpload,
                input     : []
            }
        } );
    }

    onUpload() {
        const {
                  elusiveData
              }                     = this.props,
              xml                   = elusiveData?.xml ?? '' as string | string[],
              uploadDataHandler     = async ( ...args : Parameters<UploadData> ) => {
                  return this.props.uploadData( ...args );
              },
              setParamHandler       = ( ...args : Parameters<SetParam> ) => {
                  this.props.setParam( ...args );
              },
              setElusiveDataHandler = ( ...args : Parameters<SetElusiveData> ) => {
                  this.props.setElusiveData( ...args );
              };

        void XmlImport.doUpload(
            this.props,
            elusiveData,
            xml,
            uploadDataHandler,
            setParamHandler,
            setElusiveDataHandler
        );
    }


    /* private ****************************************************************************************************************************/

    afterRender() {
        const {
                  uid
              }                     = this.props,
              {
                  errorCount
              }                     = this.state,
              toolbar               = document.getElementById( ( `${uid}_toolbar` ) ) as HTMLElement,
              toolbarHeight         = toolbar?.offsetHeight ?? 0,
              dataTable             = document.getElementById( `${uid}_datatable` ) as HTMLElement,
              dataTableHeader       = dataTable
                  ? dataTable.querySelector( '.p-datatable-thead' ) as HTMLElement
                  : null,
              dataTableHeaderHeight = dataTableHeader?.offsetHeight ?? 0,
              toastTopOffset        = toolbarHeight + dataTableHeaderHeight,
              dataTablePagination   = dataTable
                  ? dataTable.querySelector( '.p-paginator' ) as HTMLElement
                  : null,
              toastBottomOffset     = dataTablePagination?.offsetHeight ?? 0;

        let tooltip = `<div className="yn-tooltip-header">${XmlImport.panelTabTitle}</div>`;

        if ( toastTopOffset ) {
            this.toastTopOffset = toastTopOffset;
        }
        if ( toastBottomOffset ) {
            this.toastBottomOffset = toastBottomOffset;
        }

        this.checkForIntercomMessages( this.props.intercomMessages );

        // error
        if ( errorCount ) {
            tooltip += `<div className="yn-tooltip-info mb-0">
                            <div className="yn-tooltip-info-title" style="border-bottom: none; padding-bottom: 0; margin-bottom: 0;"><span className="fas fa-exclamation-circle yn-error mr-2"></span> ${__( 'YN.base.panel.tooltip.hasError' )}</div>
                        </div>`;
        }

        this.props.setPanelInfo( uid, {
            title  : XmlImport.panelTabTitle,
            icon   : XmlImport.panelTabIcon,
            tooltip,
            dirty  : false,//( activePanel === 'input' && !!this.inputPanel?.getXml() ),
            invalid: !!errorCount,
            path   : XmlImport.buildPanelPathFromProps( this.props )
        } );
    }

    /**
     * Checks if there are intercom messages present and shows them if.
     *
     * @private
     */
    checkForIntercomMessages( intercomMessages? : PanelIntercomState ) {
        // check for toasts
        if ( this.toast && intercomMessages?.toast ) {
            const toastKeys = Object.keys( intercomMessages.toast );
            for ( const key of toastKeys ) {
                // show toast
                if ( intercomMessages.toast[ key ] ) {
                    this.toast.show( intercomMessages.toast[ key ] );
                }

                // clear toast message
                this.props.unsetMessage( this.props.uid, 'toast', key as string );
            }
        }
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  activePanel,
                  fileUploadValue,
                  inputValue,
                  elusiveData
              } = this.props,
              {
                  errorCount,
                  errorTooltip,
                  errorMessage
              } = this.state;

        return <>
            <Dialog
                header={__( 'YN.base.xmlImport.couldNotUploadWhileErrors.title' )}
                footer={<div><Button label="OK" className="p-button-outlined" onClick={() => this.props.setElusiveData( uid, { showCouldNotUploadPopup: false } )}/></div>}
                maskClassName="yn-dialog-panel-modal"
                appendTo="self"
                position="top"
                visible={elusiveData?.showCouldNotUploadPopup}
                style={{ width: 'auto', maxWidth: '80%' }}
                closable={false}
                className="yn-error-dialog"
                onHide={() => {
                }}>
                {errorMessage}
            </Dialog>
            <Toolbar
                uid={`${uid}_toolbar`}

                showUploadButton={true}
                uploadButtonErrorCount={errorCount}
                uploadButtonErrorTooltip={errorTooltip}
                onUpload={this.onUpload}
                showPanelButtons={true}
                activePanel={activePanel}
                onActivePanelChange={this.onActivePanelChange}
            />
            <div className="yn-flex-layout-parent yn-flex-layout-child">
                <Toast
                    ref={( el ) => this.toast = el}

                    position="bottom-right"
                    style={{ position: 'absolute', marginTop: this.toastTopOffset, marginBottom: this.toastBottomOffset }}
                />
                {activePanel === 'fileUpload' &&
                    <FileUploadPanel
                        uid={`${uid}_fileUploadPanel`}

                        value={fileUploadValue}
                        onValueChange={this.onFileUploadValueChange}

                        log={elusiveData?.log?.fileUpload}
                    />
                }
                {activePanel === 'input' &&
                    <InputPanel
                        uid={`${uid}_inputPanel`}

                        value={inputValue}
                        onValueChange={this.onInputValueChange}

                        log={elusiveData?.log?.input}
                    />
                }
            </div>
        </>;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
              xhrRequestsForComponent: undefined,
              intercomMessages       : undefined,
              elusiveData            : undefined,
              user                   : undefined
          },
          {
              uid,
              activePanel
          }                   = ownProps,
          activePanelUid      = `${uid}_${activePanel ?? defaultProps.activePanel}Panel`;

    // get the running xhr request belonging to this component (uid)
    if ( state.xhr?.[ uid ] ) {
        retVal.xhrRequestsForComponent = state.xhr[ uid ];
    }

    // get intercom messages
    if ( state.intercom?.[ uid ] ) {
        retVal.intercomMessages = state.intercom[ uid ];
    }

    // get elusiveData
    if ( state.elusiveData?.[ uid ] ) {
        retVal.elusiveData = {
            ...retVal.elusiveData ?? {},
            ...state.elusiveData[ uid ]
        };
    }
    if ( state.elusiveData?.[ activePanelUid ] ) {
        retVal.elusiveData = {
            ...retVal.elusiveData ?? {},
            errorData: state.elusiveData[ activePanelUid ]!.errorData,
            xml      : state.elusiveData[ activePanelUid ]!.xml
        };
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( XmlImport );
